import type { ReactNode } from 'react';
import { type LucideIcon } from 'lucide-react';
import { Skeleton } from './skeleton';
import { EmptyState } from './empty-state';
import { ErrorState } from './error-state';

export interface DataStateRegionProps<T> {
  /** RQ 에러 — `ErrorState`(재시도). */
  isError: boolean;
  /** RQ 로딩 — 스켈레톤 행. */
  isPending: boolean;
  /** 재시도 핸들러(보통 RQ refetch). */
  onRetry: () => void;
  /** 조회 결과 데이터 — 로딩/에러 시 undefined 가능. undefined 또는 `isEmpty` true 면 빈 상태. */
  data: T | undefined;
  /** 빈 판정(데이터는 있으나 0건) — 미지정 시 data 존재만으로 성공. 예: `(d) => d.rows.length === 0`. */
  isEmpty?: (data: T) => boolean;
  /** 빈 상태 제목 — 도메인에 어울리는 문구(예: "조회된 발전소가 없습니다"). */
  emptyTitle: string;
  /** 빈 상태 보조 설명(선택). */
  emptyDescription?: string;
  /** 빈 상태 아이콘(기본 Inbox) — 도메인 아이콘으로 교체 가능. */
  emptyIcon?: LucideIcon;
  /** 로딩 스켈레톤 행 수(기본 5). `skeleton` 을 주면 무시된다. */
  skeletonRows?: number;
  /**
   * 커스텀 로딩 스켈레톤(선택) — 성공 레이아웃이 **표가 아닐 때**(카드 그리드 등) 기본 행 스켈레톤 대신 쓴다.
   * 지정하면 `shell` 도 우회한다 — 성공 화면의 chrome 을 스켈레톤이 직접 흉내내야 전환 시 레이아웃이 안 튄다
   * (예: 발전소 정보 목록 = 테두리 없는 elevated 카드 2열 그리드). 에러·빈 상태는 그대로 `shell` 을 따른다.
   */
  skeleton?: ReactNode;
  /**
   * 상태(에러·로딩·빈) 영역을 표와 **동일 카드 chrome**(`rounded-xl border bg-card`)으로 감쌀지(기본 true).
   * 이미 바깥 카드 안이면 false — 이중 테두리 회피. 성공 콘텐츠(children)는 자체 chrome(표 shell)를 갖는다.
   */
  shell?: boolean;
  /** 에러·로딩·빈 상태가 채울 높이 className(예: 'h-full' = 카드 남은 높이 채움). 성공 표 높이엔 관여 안 함. */
  contentMinHeight?: string;
  /** 성공(데이터 있음) 시 렌더 — narrowed `data` 를 받는 render prop. */
  children: (data: T) => ReactNode;
}

/**
 * 데이터 영역의 상태 분기 표준 — 에러(`ErrorState`)·로딩(스켈레톤)·빈(`EmptyState`)을 표와 **동일 카드 chrome**
 * 으로 감싸 일관 표출하고, 성공 시 `children(data)`(표)를 렌더한다. "항목이 없습니다"도 표와 같은 테두리를 갖도록.
 * `DataListSection`(SimpleTable 목록)·통계 리포트(커스텀 표)가 공유 — 상태 박스 로직 중복 제거(SSOT). 빈 상태
 * 문구·아이콘은 도메인별 props. children 은 render prop 이라 `data` 를 단언 없이 narrowed 로 받는다.
 */
export function DataStateRegion<T>({
  isError,
  isPending,
  onRetry,
  data,
  isEmpty,
  emptyTitle,
  emptyDescription,
  emptyIcon,
  skeletonRows = 5,
  skeleton,
  shell = true,
  contentMinHeight,
  children,
}: DataStateRegionProps<T>): ReactNode {
  // contentMinHeight(예: 'h-full' | 'min-h-80') = 에러/빈/로딩이 카드의 남은 높이를 채우게(성공 표는 자연 높이
  // 유지 — 여긴 관여 안 함). h-full 은 외곽 box·내부 wrapper 둘 다 걸어야 부모(카드 body flex-1) 높이로 해석된다.
  // 에러·빈은 중앙정렬(center), 로딩(스켈레톤)은 위→아래 채움.
  const box = (node: ReactNode, center = false): ReactNode => {
    const filled = contentMinHeight ? (
      <div className={`${contentMinHeight}${center ? ' flex items-center justify-center' : ''}`}>
        {node}
      </div>
    ) : (
      node
    );
    const shellClass = `rounded-xl border border-border bg-card${contentMinHeight ? ` ${contentMinHeight}` : ''}`;
    return shell ? <div className={shellClass}>{filled}</div> : filled;
  };

  if (isError) return box(<ErrorState onRetry={onRetry} />, true);
  if (isPending) {
    if (skeleton) return skeleton;
    return box(
      <div className="flex flex-col gap-2 p-4">
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>,
    );
  }
  if (data === undefined || (isEmpty?.(data) ?? false)) {
    return box(
      <EmptyState title={emptyTitle} description={emptyDescription} icon={emptyIcon} />,
      true,
    );
  }
  return children(data);
}
